import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks';
import { Button } from '../../ui';
import { SubscriptionGuard } from '../../subscription';
import AuthService from '../services/AuthService';
import { USER_ROLES } from '../../../constants';
import { normalizeUserRole } from '../utils/roleUtils';
import './RouteGuards.css';

/**
 * LoadingSpinner component for authentication checks
 * @param {Object} props - Component props
 * @param {string} props.message - Loading message
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} Loading spinner component
 */
const LoadingSpinner = ({ message = 'Зареждане...', className = '' }) => (
  <div className={`arbiwin-loading-spinner ${className}`}>
    <div className="arbiwin-loading-spinner__content">
      <div className="arbiwin-loading-spinner__spinner"></div>
      <p className="arbiwin-loading-spinner__message">{message}</p>
    </div>
  </div>
);

/**
 * ErrorBoundary component for route protection errors
 * @param {Object} props - Component props
 * @param {Error} props.error - Error object
 * @param {Function} props.onRetry - Retry function
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} Error boundary component
 */
const ErrorBoundary = ({ error, onRetry, className = '' }) => (
  <div className={`arbiwin-error-boundary ${className}`}>
    <div className="arbiwin-error-boundary__content">
      <h2 className="arbiwin-error-boundary__title">Грешка при зареждане</h2>
      <p className="arbiwin-error-boundary__message">
        {error?.message || 'Възникна неочаквана грешка. Моля опитайте отново.'}
      </p>
      {onRetry && (
        <Button
          variant="primary"
          size="medium"
          onClick={onRetry}
          className="arbiwin-error-boundary__retry"
        >
          Опитайте отново
        </Button>
      )}
    </div>
  </div>
);

/**
 * ProtectedRoute component - Requires authentication
 * Redirects unauthenticated users to login page
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string} props.redirectTo - Redirect path for unauthenticated users (default: '/login')
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} Protected route component
 */
export const ProtectedRoute = ({
  children,
  redirectTo = '/login',
  className = ''
}) => {
  const { isAuthenticated, loading, error, logout } = useAuth();
  const location = useLocation();
  const [retryCount, setRetryCount] = useState(0);
  const [tokenValidating, setTokenValidating] = useState(false);

  // Validate token on mount and when authentication state changes
  useEffect(() => {
    const validateToken = async () => {
      if (!isAuthenticated || loading) return;
      
      setTokenValidating(true);
      try {
        // Check if current token is expired
        const token = localStorage.getItem('arbiwin_auth_token');
        if (token && AuthService.isTokenExpired(token)) {
          console.log('🔍 ProtectedRoute: Token is expired, logging out');
          await logout();
        }
      } catch (error) {
        console.error('🔍 ProtectedRoute: Token validation error:', error);
      } finally {
        setTokenValidating(false);
      }
    };

    validateToken();
  }, [isAuthenticated, loading, logout]);

  // Handle retry for errors
  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    // Force re-render by updating a state
  };

  // Show loading state while checking authentication or validating token
  if (loading || tokenValidating) {
    return <LoadingSpinner message="Проверка на автентичност..." className={className} />;
  }

  // Show error state if there's an authentication error
  if (error) {
    return (
      <ErrorBoundary
        error={error}
        onRetry={handleRetry}
        className={className}
      />
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // Render protected content
  return <div className={`arbiwin-protected-route ${className}`}>{children}</div>;
};

/**
 * PublicOnlyRoute component - Only for non-authenticated users
 * Redirects authenticated users to dashboard
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string} props.redirectTo - Redirect path for authenticated users (default: '/dashboard')
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} Public only route component
 */
export const PublicOnlyRoute = ({
  children,
  redirectTo = '/dashboard',
  className = ''
}) => {
  const { isAuthenticated, loading, error } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (loading) {
    return <LoadingSpinner message="Проверка на автентичност..." className={className} />;
  }

  // Show error state if there's an authentication error
  if (error) {
    return (
      <ErrorBoundary
        error={error}
        className={className}
      />
    );
  }

  // Redirect to dashboard if already authenticated
  if (isAuthenticated) {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // Render public content
  return <div className={`arbiwin-public-route ${className}`}>{children}</div>;
};

/**
 * RoleGuard component - Role-based access control
 * Checks user role and redirects if insufficient permissions
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string|Array} props.allowedRoles - Allowed roles ('User', 'Admin', 'Super Admin')
 * @param {string} props.redirectTo - Redirect path for unauthorized users (default: '/dashboard')
 * @param {boolean} props.requireAllRoles - Whether user must have ALL roles (default: false)
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} Role guard component
 */
export const RoleGuard = ({
  children,
  allowedRoles = ['User'],
  redirectTo = '/dashboard',
  requireAllRoles = false,
  className = ''
}) => {
  const { isAuthenticated, loading, error, user } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (loading) {
    return <LoadingSpinner message="Проверка на права..." className={className} />;
  }

  // Show error state if there's an authentication error
  if (error) {
    return (
      <ErrorBoundary
        error={error}
        className={className}
      />
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // Check role permissions (normalize USER/ADMIN/SUPER_ADMIN ↔ User/Admin/Super Admin)
  const userRole = normalizeUserRole(user?.role);
  const rolesArray = (Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles])
    .map((role) => normalizeUserRole(role));
  
  let hasPermission = false;
  
  if (requireAllRoles) {
    // User must have ALL specified roles
    hasPermission = rolesArray.every(role => userRole === role);
  } else {
    // User must have at least ONE of the specified roles
    hasPermission = rolesArray.includes(userRole);
  }

  // Redirect if user doesn't have required permissions
  if (!hasPermission) {
    return (
      <Navigate
        to={redirectTo}
        state={{ 
          from: location.pathname, 
          reason: 'insufficient_permissions',
          requiredRoles: rolesArray,
          userRole: userRole
        }}
        replace
      />
    );
  }

  // Render content for authorized users
  return <div className={`arbiwin-role-guard ${className}`}>{children}</div>;
};

/**
 * AdminRoute component - Shortcut for admin-only routes
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string} props.redirectTo - Redirect path for non-admin users (default: '/dashboard')
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} Admin route component
 */
export const AdminRoute = ({
  children,
  redirectTo = '/dashboard',
  className = ''
}) => (
  <RoleGuard
    allowedRoles={[USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN]}
    redirectTo={redirectTo}
    className={className}
  >
    {children}
  </RoleGuard>
);

/**
 * SuperAdminRoute component - Shortcut for super admin-only routes
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string} props.redirectTo - Redirect path for non-super-admin users (default: '/dashboard')
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} Super admin route component
 */
export const SuperAdminRoute = ({
  children,
  redirectTo = '/dashboard',
  className = ''
}) => (
  <RoleGuard
    allowedRoles={[USER_ROLES.SUPER_ADMIN]}
    redirectTo={redirectTo}
    className={className}
  >
    {children}
  </RoleGuard>
);

/**
 * SubscriptionRoute component - Requires active subscription
 * Uses the dedicated SubscriptionGuard component for subscription validation
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string} props.redirectTo - Redirect path for users without subscription (default: '/subscription')
 * @param {string} props.loginRedirectTo - Redirect path for unauthenticated users (default: '/login')
 * @param {boolean} props.showError - Whether to show error messages (default: true)
 * @param {Function} props.onSubscriptionCheck - Callback when subscription check completes
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} Subscription route component
 */
export const SubscriptionRoute = ({
  children,
  redirectTo = '/subscription',
  loginRedirectTo = '/login',
  showError = true,
  onSubscriptionCheck,
  className = ''
}) => (
  <SubscriptionGuard
    redirectTo={redirectTo}
    loginRedirectTo={loginRedirectTo}
    showError={showError}
    onSubscriptionCheck={onSubscriptionCheck}
    className={className}
  >
    {children}
  </SubscriptionGuard>
);

export default {
  ProtectedRoute,
  PublicOnlyRoute,
  RoleGuard,
  AdminRoute,
  SuperAdminRoute,
  SubscriptionRoute,
  LoadingSpinner,
  ErrorBoundary
};
